import { useEffect, useRef, useState } from 'react';
import type { CustomizationDraft, Key, PiclickWP } from '@/types/piclick';
import {
  WIZARD_STEPS,
  calculateTotal,
  createInitialDraft,
  getStepBlocker,
  isStepValid,
  resizeKeys,
} from '@/lib/customization';
import { addCustomizationToCart, AddToCartError } from '@/lib/cart';
import { formatBRL } from '@/lib/price';
import { Visualizer } from './Visualizer';
import { BaseSection } from './BaseSection';
import { KeysSection } from './KeysSection';
import { AccessorySection } from './AccessorySection';
import { ReviewStep } from './ReviewStep';
import { WizardNav } from './WizardNav';
import { RealPhotosGallery } from './RealPhotosGallery';

type Props = {
  data: PiclickWP;
};

const STEP_META = {
  base: { title: 'Escolha a Base', description: 'Tamanho e cor da estrutura.' },
  keys: { title: 'Personalize as Teclas', description: 'Cores, letras ou ícones.' },
  accessory: { title: 'Escolha o Acessório', description: 'A ferragem ideal pro seu estilo.' },
  review: { title: 'Revisar', description: 'Confira e adicione à mochila.' },
} as const;

export function Customizer({ data }: Props) {
  const [draft, setDraft] = useState<CustomizationDraft>(createInitialDraft);
  const [stepIndex, setStepIndex] = useState(0);
  const [openKeyIndex, setOpenKeyIndex] = useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const currentStep = WIZARD_STEPS[stepIndex];
  const total = calculateTotal(draft, data);
  const canAdvance = isStepValid(currentStep, draft);
  const blocker = getStepBlocker(currentStep, draft);
  const isLast = stepIndex === WIZARD_STEPS.length - 1;

  const setSize = (sizeId: string) => {
    const size = data.sizes.find((s) => s.id === sizeId);
    if (!size) return;
    setDraft((prev) => ({
      ...prev,
      sizeId,
      keys: resizeKeys(prev.keys, size.keys),
    }));
  };

  const setBaseColor = (baseColorId: string) => {
    setDraft((prev) => ({ ...prev, baseColorId }));
  };

  const setKeys = (keys: Key[]) => {
    setDraft((prev) => ({ ...prev, keys }));
  };

  const setAccessory = (accessoryId: string) => {
    setDraft((prev) => ({ ...prev, accessoryId }));
  };

  const handleKeyClickInPreview = (index: number) => {
    if (currentStep !== 'keys') {
      const keysIdx = WIZARD_STEPS.indexOf('keys');
      setStepIndex(keysIdx);
    }
    setOpenKeyIndex(index);
    requestAnimationFrame(() => {
      const el = document.getElementById(`key-${index}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const handleAddToCart = async () => {
    if (isSubmitting) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await addCustomizationToCart(draft, data);
      window.location.assign(data.cartUrl);
    } catch (err) {
      const message =
        err instanceof AddToCartError
          ? err.message
          : 'Não foi possível adicionar à mochila. Tente de novo.';
      setSubmitError(message);
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isLast) {
      handleAddToCart();
      return;
    }
    setStepIndex((i) => Math.min(WIZARD_STEPS.length - 1, i + 1));
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex]);

  const meta = STEP_META[currentStep];

  return (
    <div className="piclick-customizer min-h-[calc(100vh-200px)] bg-cream">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div
          className="sticky z-30 -mx-4 border-b-2 border-plum/10 bg-cream/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden"
          style={{ top: 'var(--pc-header-height)' }}
        >
          <Visualizer
            draft={draft}
            options={data}
            onKeyClick={handleKeyClickInPreview}
            compact
          />
        </div>

        <div className="grid gap-8 py-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,500px)] lg:gap-12 lg:py-14">
          <div
            className="hidden lg:sticky lg:block lg:self-start"
            style={{ top: 'calc(var(--pc-header-height) + 1.5rem)' }}
          >
            <Visualizer
              draft={draft}
              options={data}
              onKeyClick={handleKeyClickInPreview}
              compact
            />
          </div>

          <div className="space-y-7">
          <header className="px-1">
            <span className="inline-block rounded-full border-[3px] border-plum bg-butter px-4 py-1.5 font-action text-[0.8rem] font-bold uppercase tracking-wider text-plum shadow-[0_3px_0_0_#600b2e]">
              Passo {stepIndex + 1} de {WIZARD_STEPS.length}
            </span>
            <h1
              className="font-action text-2xl font-bold leading-tight text-plum sm:text-3xl"
              style={{ marginTop: '1.25rem' }}
            >
              {meta.title}
            </h1>
            <p
              className="font-action text-sm text-plum/70"
              style={{ marginTop: '0.5rem' }}
            >
              {meta.description}
            </p>
          </header>

          <section className="rounded-[1.75rem] border-2 border-plum/15 bg-white p-6 sm:p-8">
            {currentStep === 'base' && (
              <BaseSection
                sizes={data.sizes}
                baseColors={data.baseColors}
                selectedSizeId={draft.sizeId}
                selectedBaseColorId={draft.baseColorId}
                onSelectSize={setSize}
                onSelectBaseColor={setBaseColor}
              />
            )}
            {currentStep === 'keys' && (
              <KeysSection
                keys={draft.keys}
                symbols={data.symbols}
                keyColors={data.keyColors}
                openIndex={openKeyIndex}
                onOpenChange={setOpenKeyIndex}
                onChange={setKeys}
              />
            )}
            {currentStep === 'accessory' && (
              <AccessorySection
                accessories={data.accessories}
                selected={draft.accessoryId}
                onSelect={setAccessory}
              />
            )}
            {currentStep === 'review' && (
              <ReviewStep draft={draft} options={data} total={total} />
            )}
          </section>

          <div className="rounded-[1.5rem] border-2 border-plum/15 bg-white p-5 sm:p-6">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <div className="font-action text-[11px] font-bold uppercase tracking-wide text-plum/70">
                  Total parcial
                </div>
                <div className="mt-1 font-action text-2xl font-bold tabular-nums leading-none text-plum sm:text-3xl">
                  {formatBRL(total)}
                </div>
              </div>
            </div>

            {submitError && (
              <p
                role="alert"
                className="mb-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-medium text-red-800"
              >
                {submitError}
              </p>
            )}

            <WizardNav
              currentStep={stepIndex}
              totalSteps={WIZARD_STEPS.length}
              canAdvance={canAdvance && !isSubmitting}
              blockerMessage={blocker}
              onBack={handleBack}
              onNext={handleNext}
              nextLabel={
                isLast
                  ? isSubmitting
                    ? 'Adicionando…'
                    : 'Adicionar à Mochila'
                  : 'Avançar'
              }
            />
          </div>
          </div>
        </div>

        <RealPhotosGallery photos={data.realPhotos ?? []} />
      </div>
    </div>
  );
}
